// src/components/VendorDetail.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format, parseISO, isValid as isValidDateFn } from 'date-fns';
import api from '../services/api';

// --- Constants ---
const NA_STYLE_CLASSES = "text-gray-400 italic";

// --- Helper Functions ---
const formatDateInternal = (dateString) => {
  if (!dateString) return null;
  try {
    const date = parseISO(dateString);
    return isValidDateFn(date) ? format(date, 'MMM d, yyyy') : { error: "Invalid Date" };
  } catch (e) {
    console.error("Error parsing date:", dateString, e);
    return { error: "Date Parse Error" };
  }
};

const displayListInternal = (list) => {
  if (Array.isArray(list) && list.length > 0) {
    return list.join(', ');
  }
  return null;
};

// --- Icon Components (using your exact SVGs) ---
const PencilIcon = ({ className = "w-4 h-4 mr-2" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path d="M5.433 13.917l1.262-3.832a.75.75 0 011.139-.244l4.745 4.745a.75.75 0 01-.244 1.139l-3.832 1.262a.75.75 0 01-.926-.926zM10 15a5 5 0 100-10 5 5 0 000 10z" />
  </svg>
);

const TrashIcon = ({ className = "w-4 h-4 mr-2" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.58.275-2.333.593L2.25 4.822a.75.75 0 000 1.154l.114.095c.783.694 1.484 1.057 1.952 1.171V15A2.75 2.75 0 007.75 17h4.5A2.75 2.75 0 0015 14.25V7.238c.468-.114 1.169-.477 1.952-1.171l.114-.095a.75.75 0 000-1.154l-1.416-.732c-.753-.318-1.538-.516-2.333-.593V3.75C14 2.231 12.769 1 11.25 1h-2.5zM10 4c.828 0 1.5.672 1.5 1.5S10.828 7 10 7s-1.5-.672-1.5-1.5S9.172 4 10 4zm2 10.25a.75.75 0 00.75-.75v-4.5a.75.75 0 00-1.5 0v4.5a.75.75 0 00.75.75zm-3.5 0a.75.75 0 00.75-.75v-4.5a.75.75 0 00-1.5 0v4.5a.75.75 0 00.75.75z" clipRule="evenodd" />
  </svg>
);

const EmailIcon = ({ className = "w-4 h-4 mr-2 text-gray-500" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path d="M.05 3.555A2 2 0 012 2h16a2 2 0 011.95 1.555L10 11.233zM2 6.82v7.73A2 2 0 004 16h12a2 2 0 002-1.45V6.82L10 13.078z" />
  </svg>
);

const UserPhoneIcon = ({ className = "w-4 h-4 mr-2 text-gray-500" }) => ( // This is your original "phone" icon
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path fillRule="evenodd" d="M2 3.5A1.5 1.5 0 013.5 2h13A1.5 1.5 0 0118 3.5v13a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 012 16.5v-13zm3 4.5A1 1 0 016 7h8a1 1 0 010 2H6a1 1 0 01-1-1zm0 3a1 1 0 011-1h8a1 1 0 010 2H6a1 1 0 01-1-1zm0 3a1 1 0 011-1h8a1 1 0 010 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
  </svg>
);

const LinkIcon = ({ className = "w-4 h-4 mr-2" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path d="M12.232 4.232a2.5 2.5 0 013.536 3.536l-1.018 1.018a.75.75 0 001.06 1.06l1.019-1.018a4 4 0 00-5.656-5.656l-1.019 1.018a.75.75 0 001.06 1.06l1.018-1.018z" />
    <path d="M3.75 12.25a.75.75 0 001.06 0l.97-.97A4 4 0 0110 5.657l.97-.97a.75.75 0 000-1.06l-.97-.97A5.5 5.5 0 003.75 12.25zm.5 1.5a.75.75 0 00-1.06 0l-.97.97A4 4 0 0010 18.343l.97.97a.75.75 0 000-1.06l-.97-.97a5.5 5.5 0 00-6.25-6.25z" />
  </svg>
);

const ArrowUturnLeftIcon = ({ className = "w-5 h-5 mr-2" }) => ( // A proper U-turn Left icon
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
  </svg>
);

const PlusIcon = ({ className = "w-4 h-4 mr-2" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
        <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
    </svg>
);

// --- UI Sub-components ---
const LoadingIndicator = () => (
  <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-gray-100">
    <div className="bg-white shadow-lg rounded-xl p-8 max-w-md w-full text-center text-indigo-700">
      <svg className="animate-spin h-12 w-12 text-indigo-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <p className="text-xl font-semibold">Loading vendor details...</p>
      <p className="mt-2 text-sm text-gray-500">Please wait a moment while we fetch the information.</p>
    </div>
  </div>
);

const ErrorDisplay = ({ errorMessage }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-gray-100">
      <div className="bg-white shadow-lg rounded-xl p-8 max-w-md w-full text-center text-red-700 border border-red-300">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 text-red-500 mx-auto mb-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.174 3.35 1.94 3.35h14.72c1.766 0 2.806-1.85 1.94-3.35L12 2.25 2.697 15.626zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        <p className="text-xl font-semibold">Error Loading Data</p>
        <p className="mt-2 text-base text-gray-600">{errorMessage}</p>
        <button onClick={() => navigate('/vendors')} className="mt-6 inline-flex justify-center items-center py-2.5 px-5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-200 ease-in-out">
          Go Back to Vendors
        </button>
      </div>
    </div>
  );
};

const NotFoundDisplay = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-gray-100">
      <div className="bg-white shadow-lg rounded-xl p-8 max-w-md w-full text-center text-gray-700 border border-gray-300">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 text-gray-500 mx-auto mb-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
        <p className="text-xl font-semibold">Vendor Not Found</p>
        <p className="mt-2 text-base text-gray-600">The vendor you are looking for could not be found or the data is empty.</p>
        <button onClick={() => navigate('/vendors')} className="mt-6 inline-flex justify-center items-center py-2.5 px-5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-200 ease-in-out">
          Back to Vendors List
        </button>
      </div>
    </div>
  );
};

const DetailItem = ({ label, value, valueProcessor }) => {
  let displayValue;
  const processedValue = valueProcessor ? valueProcessor(value) : value;

  if (processedValue === null || processedValue === undefined || (typeof processedValue === 'string' && processedValue.trim() === '')) {
    displayValue = <span className={NA_STYLE_CLASSES}>N/A</span>;
  } else if (typeof processedValue === 'object' && processedValue !== null && processedValue.error) {
    displayValue = <span className="text-red-500 italic">{processedValue.error}</span>;
  } else {
    displayValue = processedValue;
  }

  return (
    <div className="py-3 sm:py-4 px-4 sm:px-6 border-b border-gray-100 last:border-b-0 group transition-colors duration-150 hover:bg-gray-50 flex flex-col sm:flex-row sm:justify-between sm:items-center">
      <dt className="text-sm font-medium text-gray-600 sm:w-1/3">{label}</dt>
      <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:w-2/3 break-words">{displayValue}</dd>
    </div>
  );
};

const VendorPageHeader = ({ vendorName, vendorId, onDelete, isLoading }) => (
  <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-6 py-6 sm:px-8 border-b border-gray-200">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
      <div>
        <h3 className="text-3xl leading-tight font-extrabold text-white sm:text-4xl">
          {vendorName || <span className="text-indigo-200 italic">Unnamed Vendor</span>}
        </h3>
        <p className="mt-2 text-md text-indigo-100 opacity-90">
          Detailed profile and management options for this vendor.
        </p>
      </div>
      <div className="mt-4 sm:mt-0 flex-shrink-0 flex flex-wrap gap-3">
        {/* "Create RFQ" button has been removed */}
        <Link to={`/vendors/${vendorId}/edit`} className="inline-flex items-center px-4 py-2 border border-white rounded-md shadow-sm text-sm font-medium text-white bg-transparent hover:bg-white hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white transition duration-200 ease-in-out transform hover:scale-105">
          <PencilIcon /> Edit
        </Link>
        <button onClick={onDelete} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-200 ease-in-out transform hover:scale-105" disabled={isLoading}>
          <TrashIcon /> Delete
        </button>
      </div>
    </div>
  </div>
);

const ContactItem = ({ contact, isPrimary }) => (
  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 shadow-sm transition-all duration-200 hover:bg-gray-100 hover:shadow-md">
    <p className="flex items-center text-gray-900 font-semibold text-base mb-1">
      {contact.name || <span className={NA_STYLE_CLASSES}>Unnamed Contact</span>}
      {isPrimary && <span className="ml-3 px-2.5 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-full">Primary</span>}
    </p>
    {contact.email && (
      <p className="mt-1 flex items-center text-sm text-gray-700">
        <EmailIcon /> {contact.email}
      </p>
    )}
    {contact.mobile && (
      <p className="mt-1 flex items-center text-sm text-gray-700">
        <UserPhoneIcon /> {contact.mobile}
      </p>
    )}
  </div>
);

const DocumentLinkItem = ({ label, url }) => {
  const fileName = url ? (url.split('/').pop() || 'link') : '';
  const documentName = label.split(' ')[0];

  return (
    <DetailItem
      label={label}
      value={
        url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-indigo-600 hover:text-indigo-800 hover:underline transition duration-150 ease-in-out font-medium">
            <LinkIcon /> View {documentName}
            <span className="text-gray-500 text-xs ml-1">({fileName})</span>
          </a>
        ) : (
          <span className={`${NA_STYLE_CLASSES} text-sm`}>No file uploaded</span>
        )
      }
    />
  );
};

const VendorPageFooter = () => {
  const navigate = useNavigate();
  return (
    <div className="px-6 py-4 bg-gray-50 text-right sm:px-8 border-t border-gray-200">
      <button type="button" onClick={() => navigate('/vendors')} className="inline-flex justify-center items-center py-2.5 px-5 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-200 ease-in-out transform hover:scale-105">
        <ArrowUturnLeftIcon /> Back to Vendors List
      </button>
    </div>
  );
};

const VendorDetail = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!vendorId) {
      setIsLoading(false);
      setError("Vendor ID is missing.");
      return;
    }

    const fetchVendor = async () => {
      setIsLoading(true);
      setError(null);
      setVendor(null);
      try {
        const response = await api.get(`vendors/${vendorId}/`);
        setVendor(response.data);
      } catch (err) {
        console.error("Error fetching vendor details:", err);
        let errorMsg = "Failed to load vendor details.";
        if (err.response) {
          errorMsg = err.response.data?.detail || JSON.stringify(err.response.data) || err.response.statusText || `Error ${err.response.status}`;
        } else if (err.request) {
          errorMsg = "No response from server. Check network or backend.";
        } else {
          errorMsg = err.message;
        }
        setError(errorMsg);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVendor();
  }, [vendorId]);

  const handleDelete = useCallback(async () => {
    if (window.confirm('Are you sure you want to delete this vendor? This action cannot be undone.')) {
      setIsLoading(true);
      try {
        await api.delete(`vendors/${vendorId}/`);
        alert('Vendor deleted successfully.');
        navigate('/vendors');
      } catch (err) {
        console.error("Error deleting vendor:", err);
        alert(err.response?.data?.detail || err.message || "Failed to delete vendor.");
        setIsLoading(false);
      }
    }
  }, [vendorId, navigate]);

  if (isLoading && !vendor) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorDisplay errorMessage={error} />;
  }

  if (!vendor) {
    return <NotFoundDisplay />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white shadow-2xl rounded-xl overflow-hidden border border-gray-200">
        <VendorPageHeader
          vendorName={vendor.name}
          vendorId={vendor.id}
          onDelete={handleDelete}
          isLoading={isLoading}
        />
        <div className="bg-white p-4 sm:p-6">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0 border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            {/* General Information */}
            <DetailItem label="Vendor Type" value={vendor.vendor_type} valueProcessor={displayListInternal} />
            <DetailItem label="Countries" value={vendor.countries} valueProcessor={displayListInternal} />
            {Array.isArray(vendor.countries) && vendor.countries.includes('UAE') && (
              <>
                <DetailItem label="UAE Cities Covered" value={vendor.uae_cities} valueProcessor={displayListInternal} />
                <DetailItem label="UAE Offices" value={vendor.uae_offices} valueProcessor={displayListInternal} />
              </>
            )}
            <DetailItem label="Expertise" value={vendor.expertise} />

            {/* Capabilities */}
            <DetailItem label="Data Collection Methods" value={vendor.data_collection_methods} valueProcessor={displayListInternal} />
            {Array.isArray(vendor.data_collection_methods) && vendor.data_collection_methods.includes('CATI') && (
              <DetailItem label="CATI Stations" value={vendor.cati_stations} />
            )}
            <DetailItem label="Fieldworkers" value={vendor.fieldworkers_count} />
            <DetailItem label="Supervisors" value={vendor.supervisors_count} />
            <DetailItem label="Project Managers" value={vendor.managers_count} />
            <DetailItem label="Gov. Permits (Field Team)" value={vendor.gov_permits_count} />

            {/* Urgent Requests */}
            <DetailItem label="Manages Urgent Requests" value={vendor.can_manage_urgent_requests ? 'Yes' : 'No'} />

            {/* Contacts Section */}
            <div className="col-span-full py-3 sm:py-4 px-4 sm:px-6 border-b border-gray-100 last:border-b-0 group transition-colors duration-150 hover:bg-gray-50">
              <dt className="text-sm font-medium text-gray-600 mb-2">Contacts</dt>
              <dd className="space-y-3">
                {(vendor.contacts && Array.isArray(vendor.contacts) && vendor.contacts.length > 0) ? (
                  vendor.contacts.map((contact, index) => (
                    <ContactItem key={contact.id || index} contact={contact} isPrimary={index === 0} />
                  ))
                ) : (
                  <span className={`${NA_STYLE_CLASSES} text-sm`}>No contacts listed.</span>
                )}
              </dd>
            </div>

            {/* Documents Section */}
            <DocumentLinkItem label="Trade License" url={vendor.trade_license_url} />
            <DocumentLinkItem label="Credentials Document" url={vendor.credentials_document_url} />

            {/* Audit Trail Section */}
            <DetailItem label="Created By" value={vendor.created_by_username} />
            <DetailItem label="Created At" value={vendor.created_at} valueProcessor={formatDateInternal} />
            <DetailItem label="Last Updated By" value={vendor.updated_by_username} />
            <DetailItem label="Last Updated At" value={vendor.updated_at} valueProcessor={formatDateInternal} />
          </dl>
        </div>
        <VendorPageFooter />
      </div>
    </div>
  );
};

export default VendorDetail;